import axios from "axios";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";
import { isArray, isEmpty, isNil, size } from "lodash";

const ROUTE_PREFIX = "checklists";

export const useChecklistRouteHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const checklistRouteLoading = loading;
  const error = axiosError;

  const getChecklistTasksList = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/underwriting/tasklist_templates`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Checklist Task Lists",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createNewTaskList = async ({
    dealId,
    taskListId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // if (isNil(dealId) || isEmpty(taskListId)) {
    //   console.error("Task list data is missing", { dealId, taskListId });
    //   return;
    // }

    validateRequiredFields({ dealId, taskListId });
    const endpoint = `${ROUTE_PREFIX}/create_uw_tasklist/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Loan created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const getTaskListById = async ({
    dealId,
    taskListId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, taskListId });

    // if (isNil(dealId) || isEmpty(taskListId)) {
    //   console.error("Task list data is missing", { dealId, taskListId });
    //   return;
    // }
    const endpoint = `${ROUTE_PREFIX}/retrieve_uw_tasklist/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Task List By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateTaskData = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onSuccessMsg = "Task updated successfully",
    onCompleteFn,
    onFailFn,
  }) => {
    // if (isNil(dealId) || isNil(data) || isEmpty(data)) {
    //   console.error("Data is missing", { dealId, data });
    //   return;
    // }
    validateRequiredFields({ dealId, taskListId, taskId, data });
    const endpoint = `${ROUTE_PREFIX}/update_task/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Task Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const bulkUpdateTaskData = async ({
    dealId,
    taskListId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, taskListId, data });
    const endpoint = `${ROUTE_PREFIX}/bulk_update_tasks/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Bulk Update Checklist Tasks",
      path: endpoint,
      payload: data, // typically an array of JSON objects
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: `Updated ${size(data)} Checklist Tasks`,
      onFailFn,
    });
  };

  const createChecklistComment = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(taskListId) || isNil(taskId) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { dealId, taskListId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_task_comment/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();

    // console.log("createChecklistComment", data);
    // return;
    await axiosPostRequest({
      name: "Create Checklist Task Comment",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: testing,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistCommentWithFiles = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (
      isNil(dealId) ||
      isNil(taskListId) ||
      isNil(taskId) ||
      isNil(data) ||
      isEmpty(data)
    ) {
      console.error("Loan ID or Data is missing", { dealId, data });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/create_task_comment_with_uploads/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();
    // console.log("createChecklistCommentWithFiles", {
    //   dealId,
    //   taskListId,
    //   data,
    // });
    // // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      formData:
        isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata)
          ? true
          : false,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistTaskException = async ({
    dealId,

    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/create_task_exception_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Checklist Task Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistHardException = async ({
    dealId,
    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/create_hard_exception_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Checklist Hard Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateChecklistException = async ({
    dealId,
    exceptionId,
    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data, exceptionId });
    const endpoint = `${ROUTE_PREFIX}/update_exception_request/${dealId}/${exceptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Checklist Hard Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const getLoanUnderwritingRequirements = async ({
    dealId,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/retrieve_underwriting_requirements/${dealId} `;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: `Get Loan Underwriting Requirements`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const createLoanUnderwritingRequirement = async ({
    dealId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      data,
    });
    const endpoint = `${ROUTE_PREFIX}/create_underwriting_requirement/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Underwriting Requirement`,
      path: endpoint,
      payload: data,

      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const createLoanUnderwritingRequirementWithFile = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      data,
    });
    const endpoint = `${ROUTE_PREFIX}/create_underwriting_requirement_with_upload/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Underwriting Requirement With File`,
      path: endpoint,
      payload: data,
      formData:
        isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata)
          ? true
          : false,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanUnderwritingRequirement = async ({
    dealId,
    requirementId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      requirementId,
      data,
    });
    const endpoint = `${ROUTE_PREFIX}/update_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Update Underwriting Requirement`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Underwriting Requirement Updated",
      onFailFn,
    });
  };

  const addCommentToUnderwritingException = async ({
    dealId,
    payload,
    excecptionId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ payload, dealId, excecptionId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_exception/${dealId}/${excecptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Exception",
      // payload: {
      //   htmlString: commentText,
      // },
      payload,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  /**
   * ########## UNDERWRITING REQUIREMENTS ###########
   */
  const addCommentToUnderwritingRequirement = async ({
    commentText,
    dealId,
    requirementId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ commentText, dealId, requirementId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Requirement",
      payload: {
        htmlString: commentText,
      },
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  const uploadFilesToUnderwritingRequirement = async ({
    files,
    dealId,
    requirementId,
    payload,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ files, dealId, requirementId });

    const endpoint = `${ROUTE_PREFIX}/upload_loan_docs_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Documents To UW Requirement",
      path: endpoint,
      files: files,
      formData: true, // For file uploads
      payload,
      cancelSource: source,
      // errorTesting: true,
      onSuccessFn,
      onSuccessMsg:
        size(files) > 1
          ? "Documents uploaded successfully"
          : "Document uploaded successfully",
      onCompleteFn,
      onFailFn,
    });
  };
  return {
    error,
    loading,
    checklistRouteLoading,
    getChecklistTasksList,
    createNewTaskList,
    getTaskListById,
    updateTaskData,
    createChecklistCommentWithFiles,
    createChecklistComment,
    bulkUpdateTaskData,
    createChecklistTaskException,
    createChecklistHardException,
    updateChecklistException,
    getLoanUnderwritingRequirements,
    updateLoanUnderwritingRequirement,
    createLoanUnderwritingRequirement,
    createLoanUnderwritingRequirementWithFile,
    uploadFilesToUnderwritingRequirement,
    addCommentToUnderwritingRequirement,
    addCommentToUnderwritingException,
  };
};
